import { ReplayPlayerBlock } from '@/components/admin/reglages/replay-player-block';
import { ReplayTextField } from '@/components/admin/reglages/replay-text-field';
import { ReplayUnsavedBox } from '@/components/admin/reglages/replay-unsaved-box';
import type { ModelCatalog } from '@/lib/model-catalog';
import {
  REPLAY_TEXT_FIELDS,
  type ReplayTextField as ReplayTextFieldSpec,
  authorLabel,
  playerOf,
  playerProblem,
  sameSeat,
  saveInputOf,
  textProblems,
  textsOf,
  touchedKeys,
} from '@/lib/replay-settings';
import { versionedClock, versionedDay } from '@/lib/versioned-labels';
import type {
  ModelSeat,
  ModelTariff,
  ReplaySettings,
  ReplaySettingsInput,
  ReplayTexts,
} from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * « La consigne du joueur » (docs/specs/rejeu.md §10.3): the three texts Claude
 * is served when a replay starts, under the line that says since when they are
 * in force, and the box that appears when one of them moves.
 *
 * ## The draft is the screen's, and it is initialised from props - never by an effect
 *
 * The régime of the sections of Réglages > Règles: the three fields and the
 * reason are state of THIS component, seeded from `settings` once, and « Revenir
 * aux textes en vigueur » puts the draft back on what the props hold NOW. The
 * page remounts the panel (a `key`) after a save that landed, which is what
 * drops a draft that has just become the text in force. A refetch in the
 * background must NOT do that: a colleague saving while someone types would
 * otherwise throw their paragraph away, and `settings` simply moves under the
 * draft - the fields then read as « touched » against the new text, which is the
 * truth.
 *
 * ## One request, three texts - and the model of the player
 *
 * The instruction is one document of three parts (§10.1); a save replaces the
 * three at once, so a text left untouched travels with its siblings and the
 * server writes one line of the journal for the move.
 *
 * The model of the player of the server (npc.md §13.4, N7, H28) is the block over the texts, held
 * in the same draft and saved by the same button: it travels in the request ONLY when it moved,
 * since an absent model is an unchanged one, and the journal line says `player` only then.
 */

export interface ReplaySettingsPanelProps {
  /** The texts in force, with who saved them and when, and how many replays were played. */
  readonly settings: ReplaySettings;
  readonly saving: boolean;
  /** The sentence of the last failed save, or null. */
  readonly failure: string | null;
  /** The tiles and the rates the block of the player draws on; the built-in ones until they land. */
  readonly catalog: ModelCatalog;
  readonly tariffs: readonly ModelTariff[];
  readonly catalogSource: string;
  readonly onSave: (input: ReplaySettingsInput) => void;
  /** The draft went back to the texts in force: forget the failure that was about it. */
  readonly onRevert: () => void;
}

export function ReplaySettingsPanel({
  settings,
  saving,
  failure,
  catalog,
  tariffs,
  catalogSource,
  onSave,
  onRevert,
}: ReplaySettingsPanelProps) {
  const [draft, setDraft] = useState<ReplayTexts>(() => textsOf(settings));
  const [playerDraft, setPlayerDraft] = useState<ModelSeat>(() => playerOf(settings));
  const [note, setNote] = useState('');

  const inForce = textsOf(settings);
  const touched = touchedKeys(inForce, draft);
  const problems = textProblems(draft);
  const playerInForce = playerOf(settings);
  const playerMoved = !sameSeat(playerInForce, playerDraft);
  const blocked = Object.keys(problems).length > 0 || playerProblem(playerDraft) !== null;

  function renderField(spec: ReplayTextFieldSpec) {
    return (
      <ReplayTextField
        key={spec.key}
        field={spec}
        value={draft[spec.key]}
        touched={touched.includes(spec.key)}
        problem={problems[spec.key] ?? null}
        disabled={saving}
        onChange={(value) => setDraft((current) => ({ ...current, [spec.key]: value }))}
      />
    );
  }

  return (
    <>
      <ReplayPlayerBlock
        value={playerDraft}
        stored={playerInForce}
        touched={playerMoved}
        disabled={saving}
        catalog={catalog}
        tariffs={tariffs}
        catalogSource={catalogSource}
        onChange={setPlayerDraft}
      />

      <section
        aria-labelledby="rejeu-consigne-titre"
        data-testid="rejeu-consigne"
        className="flex flex-col gap-5 rounded-lg border-2 border-gray-200 px-5 py-4"
      >
        <div className="flex flex-col gap-1.5">
          <h3 id="rejeu-consigne-titre" className="font-bold text-bleu-nuit text-h4">
            La consigne du joueur
          </h3>
          <p data-testid="rejeu-en-vigueur" className="text-gray-600 text-small">
            En vigueur depuis le {versionedDay(settings.updatedAt)} à{' '}
            {versionedClock(settings.updatedAt)} ({authorLabel(settings.updatedBy)}).
          </p>
        </div>

        {REPLAY_TEXT_FIELDS.filter((spec) => spec.key === 'rules').map(renderField)}

        <div className="grid gap-5 md:grid-cols-2">
          {REPLAY_TEXT_FIELDS.filter((spec) => spec.key !== 'rules').map(renderField)}
        </div>
      </section>

      {touched.length === 0 && !playerMoved ? null : (
        <ReplayUnsavedBox
          touched={touched}
          player={playerMoved ? { before: playerInForce, after: playerDraft } : null}
          replayCount={settings.replayCount}
          note={note}
          saving={saving}
          blocked={blocked}
          failure={failure}
          onNote={setNote}
          onSave={() => onSave(saveInputOf(draft, note, playerMoved ? playerDraft : null))}
          onRevert={() => {
            setDraft(textsOf(settings));
            setPlayerDraft(playerOf(settings));
            setNote('');
            onRevert();
          }}
        />
      )}
    </>
  );
}
